import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  CircularProgress,
  Alert,
  Stack,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import api from '../../utils/api';
import CountrySelectField from '../../components/common/CountrySelectField';
import H1LotteryWorkflowDrawer, { H1_LOTTERY_WF_LABELS } from './H1LotteryWorkflowDrawer';
import { useImmigrationWorkflowDefinition } from './useImmigrationWorkflowDefinition';

const PROCESS_LABELS = {
  h1_lottery: 'H1 Lottery',
  h1_transfer: 'H1 Transfer',
  h1_extension: 'H1 Extension',
};

const MONTH_OPTIONS = [
  { v: 1, label: 'January' },
  { v: 2, label: 'February' },
  { v: 3, label: 'March' },
  { v: 4, label: 'April' },
  { v: 5, label: 'May' },
  { v: 6, label: 'June' },
  { v: 7, label: 'July' },
  { v: 8, label: 'August' },
  { v: 9, label: 'September' },
  { v: 10, label: 'October' },
  { v: 11, label: 'November' },
  { v: 12, label: 'December' },
];

function contactLabel(c) {
  if (!c) return '';
  const name = `${c.first_name || ''} ${c.last_name || ''}`.trim();
  return `${name} — ${c.email || ''}`;
}

export default function ProcessH1List() {
  const { data: h1Wf } = useImmigrationWorkflowDefinition('h1_lottery');
  const h1WfLabels = { ...H1_LOTTERY_WF_LABELS, ...(h1Wf?.stepLabels || {}) };

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0 });
  const [page, setPage] = useState(1);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [processType, setProcessType] = useState('h1_lottery');
  const [contactOptions, setContactOptions] = useState([]);
  const [contactLoading, setContactLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  const [countryOfBirth, setCountryOfBirth] = useState('');
  const [countryOfPassport, setCountryOfPassport] = useState('');
  const [mastersUsa, setMastersUsa] = useState('no');
  const [graduationDate, setGraduationDate] = useState('');
  const [lotteryMonth, setLotteryMonth] = useState(3);
  const [lotteryYear, setLotteryYear] = useState(new Date().getFullYear());
  const [notes, setNotes] = useState('');

  const [workflowDrawerOpen, setWorkflowDrawerOpen] = useState(false);
  const [workflowCaseId, setWorkflowCaseId] = useState(null);

  const loadCases = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/immigration/h1-cases', { params: { page, limit: 25 } });
      setRows(res.data?.data || []);
      const p = res.data?.pagination;
      if (p) {
        setPagination({ page: p.page, totalPages: p.totalPages, total: p.total });
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load H1 cases.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  const loadContactsForType = useCallback(async (type) => {
    setContactLoading(true);
    try {
      const contactType = type === 'h1_extension' ? 'employee' : 'candidate';
      const res = await api.get('/contacts', {
        params: { contact_type: contactType, status: 'active', limit: 500, page: 1 },
      });
      setContactOptions(res.data?.data || []);
    } catch {
      setContactOptions([]);
    } finally {
      setContactLoading(false);
    }
  }, []);

  const openDialog = () => {
    setFormError('');
    setProcessType('h1_lottery');
    setSelectedContact(null);
    setCountryOfBirth('');
    setCountryOfPassport('');
    setMastersUsa('no');
    setGraduationDate('');
    setLotteryMonth(3);
    setLotteryYear(new Date().getFullYear());
    setNotes('');
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    loadContactsForType(processType);
    setSelectedContact(null);
    if (processType === 'h1_lottery') {
      const y = new Date().getFullYear();
      setLotteryMonth(3);
      setLotteryYear(y);
    }
  }, [processType, open, loadContactsForType]);

  const phoneDisplay = (c) => {
    if (!c) return '';
    return c.phone || c.mobile || '';
  };

  const submit = async () => {
    setFormError('');
    if (!selectedContact) {
      setFormError('Select a contact.');
      return;
    }
    setSaving(true);
    try {
      const body = {
        contact_id: selectedContact.id,
        process_type: processType,
        notes: notes.trim() || undefined,
      };
      if (processType === 'h1_lottery') {
        body.country_of_birth = countryOfBirth.trim();
        body.country_of_passport = countryOfPassport.trim();
        body.masters_degree_usa = mastersUsa === 'yes';
        body.graduation_date = graduationDate || undefined;
        body.lottery_month = lotteryMonth;
        body.lottery_year = lotteryYear;
      }
      await api.post('/immigration/h1-cases', body);
      setOpen(false);
      loadCases();
    } catch (e) {
      setFormError(e.response?.data?.message || 'Failed to create H1 case.');
    } finally {
      setSaving(false);
    }
  };

  const yearOptions = [];
  const cy = new Date().getFullYear();
  for (let y = cy - 2; y <= cy + 3; y += 1) yearOptions.push(y);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Process H1
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Track H1 Lottery, H1 Transfer, and H1 Extension cases. New lottery rows default to March of the
        current year unless you change them.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openDialog}>
              Add
            </Button>
          </Stack>

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Candidate / employee</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Process</TableCell>
                    <TableCell>Lottery / extra</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Workflow</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No H1 cases yet. Click Add to create one.
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      const c = row.contact;
                      const name = c ? `${c.first_name || ''} ${c.last_name || ''}`.trim() : '—';
                      const lotteryExtra =
                        row.process_type === 'h1_lottery'
                          ? `${MONTH_OPTIONS.find((m) => m.v === row.lottery_month)?.label || row.lottery_month} ${row.lottery_year || ''}`
                          : '—';
                      const wfStep = row.h1_workflow_step || row.h1WorkflowStep;
                      return (
                        <TableRow key={row.id}>
                          <TableCell>{name}</TableCell>
                          <TableCell>{c?.email || '—'}</TableCell>
                          <TableCell>{c ? phoneDisplay(c) : '—'}</TableCell>
                          <TableCell>{PROCESS_LABELS[row.process_type] || row.process_type}</TableCell>
                          <TableCell>
                            {row.process_type === 'h1_lottery'
                              ? `${lotteryExtra} · Birth: ${row.country_of_birth || '—'}`
                              : '—'}
                          </TableCell>
                          <TableCell>
                            {row.createdAt || row.created_at
                              ? new Date(row.createdAt || row.created_at).toLocaleString()
                              : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {row.process_type === 'h1_lottery' ? (
                              <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center" flexWrap="wrap">
                                <Chip
                                  size="small"
                                  label={h1WfLabels[wfStep] || wfStep || '—'}
                                  color={
                                    wfStep === 'workflow_complete'
                                      ? 'success'
                                      : wfStep === 'workflow_revoked'
                                        ? 'error'
                                        : wfStep === 'lottery_not_picked'
                                          ? 'default'
                                          : 'primary'
                                  }
                                  variant={wfStep === 'lottery_not_picked' ? 'outlined' : 'filled'}
                                />
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setWorkflowCaseId(row.id);
                                    setWorkflowDrawerOpen(true);
                                  }}
                                >
                                  Open
                                </Button>
                              </Stack>
                            ) : (
                              '—'
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {pagination.totalPages > 1 && (
            <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous
              </Button>
              <Typography variant="body2">
                Page {page} of {pagination.totalPages} ({pagination.total} total)
              </Typography>
              <Button
                size="small"
                disabled={page >= pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New H1 case</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="h1-process-type">Process type</InputLabel>
              <Select
                labelId="h1-process-type"
                label="Process type"
                value={processType}
                onChange={(e) => setProcessType(e.target.value)}
              >
                <MenuItem value="h1_lottery">H1 Lottery (candidate)</MenuItem>
                <MenuItem value="h1_transfer">H1 Transfer (candidate)</MenuItem>
                <MenuItem value="h1_extension">H1 Extension (employee)</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              options={contactOptions}
              loading={contactLoading}
              getOptionLabel={(o) => contactLabel(o)}
              value={selectedContact}
              onChange={(_, v) => setSelectedContact(v)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    processType === 'h1_extension'
                      ? 'Employee contact'
                      : 'Candidate contact'
                  }
                  size="small"
                  helperText={
                    processType === 'h1_extension'
                      ? 'Only contacts with type Employee are listed.'
                      : 'Only contacts with type Candidate are listed.'
                  }
                />
              )}
            />

            {selectedContact && (
              <>
                <TextField label="First name" size="small" value={selectedContact.first_name || ''} disabled fullWidth />
                <TextField label="Last name" size="small" value={selectedContact.last_name || ''} disabled fullWidth />
                <TextField label="Email" size="small" value={selectedContact.email || ''} disabled fullWidth />
                <TextField label="Phone" size="small" value={phoneDisplay(selectedContact)} disabled fullWidth />
              </>
            )}

            {processType === 'h1_lottery' && (
              <>
                <CountrySelectField
                  label="Country of birth"
                  size="small"
                  margin="none"
                  value={countryOfBirth}
                  onChange={(v) => setCountryOfBirth(v)}
                  required
                  storeLabel
                />
                <CountrySelectField
                  label="Country of passport issued"
                  size="small"
                  margin="none"
                  value={countryOfPassport}
                  onChange={(v) => setCountryOfPassport(v)}
                  required
                  storeLabel
                />
                <FormControl>
                  <FormLabel>Masters degree in USA?</FormLabel>
                  <RadioGroup row value={mastersUsa} onChange={(e) => setMastersUsa(e.target.value)}>
                    <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
                <TextField
                  label="Graduation date"
                  type="date"
                  size="small"
                  value={graduationDate}
                  onChange={(e) => setGraduationDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                <Stack direction="row" spacing={2}>
                  <TextField
                    select
                    label="Lottery month"
                    size="small"
                    value={lotteryMonth}
                    onChange={(e) => setLotteryMonth(Number(e.target.value))}
                    fullWidth
                  >
                    {MONTH_OPTIONS.map((m) => (
                      <MenuItem key={m.v} value={m.v}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label="Lottery year"
                    size="small"
                    value={lotteryYear}
                    onChange={(e) => setLotteryYear(Number(e.target.value))}
                    fullWidth
                  >
                    {yearOptions.map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </>
            )}

            <TextField
              label="Notes (optional)"
              size="small"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <H1LotteryWorkflowDrawer
        caseId={workflowCaseId}
        open={workflowDrawerOpen}
        onClose={() => {
          setWorkflowDrawerOpen(false);
          setWorkflowCaseId(null);
        }}
        onUpdated={loadCases}
      />
    </Box>
  );
}
